import { Capacitor } from "@capacitor/core";
import { CapacitorTwilioVoice } from "@capgo/capacitor-twilio-voice";
import { encodeVoiceIdentity } from "@vidimo/shared";
import type { ReservationStatus } from "@vidimo/shared/enums";
import type React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import { toast } from "sonner";
import { interfaceTranslator } from "@/i18n/translate";
import { useApp } from "@/lib/context/use-powersync";
import type { ParsedCaller } from "./caller";
import { parseCaller } from "./caller";
import { transferCall as apiTransferCall } from "./client";
import {
  type CallSimulators,
  isSimulatedCall,
  useCallSimulators,
} from "./dev-simulators";
import { ensureMicGranted } from "./microphone";
import { useVoiceNativeListeners } from "./native-listeners";

type CallDirection = "incoming" | "outgoing";
export type CallStatus =
  | "ringing"
  | "connecting"
  | "connected"
  | "reconnecting";

/**
 * Synthetic context attached to dev-mode simulated calls. Lets the call UI
 * render the rich path (reservation pill) even when the live
 * `useCallerLookup` query misses — e.g. when seed data hasn't synced down
 * yet. Never set on real Twilio invites.
 */
export interface DevCallContext {
  reservationStatus?: ReservationStatus;
  unitCode?: string;
}

export interface ActiveCall {
  callSid: string;
  dev?: DevCallContext;
  direction: CallDirection;
  displayName?: string;
  from?: string;
  muted: boolean;
  startedAt: number | null;
  status: CallStatus;
  to?: string;
}

export interface PendingInvite {
  /**
   * The caller, parsed once at invite time from the raw Twilio fields. The
   * IncomingCall UI branches on `caller.kind` (external guest vs staff) and
   * reads already-resolved attributes (`e164`, `transport`, `displayNumber`)
   * instead of re-interpreting `from` / custom params. See {@link parseCaller}.
   */
  caller: ParsedCaller;
  callSid: string;
  dev?: DevCallContext;
}

interface VoiceContextValue extends CallSimulators {
  acceptInvite: () => Promise<void>;
  activeCall: ActiveCall | null;
  endCall: () => Promise<void>;
  makeCall: (args: { to: string; displayName?: string }) => Promise<void>;
  pendingInvite: PendingInvite | null;
  /** True when native plugin is registered with Twilio (ready for incoming calls). */
  registered: boolean;
  rejectInvite: () => Promise<void>;
  /**
   * Whether audio is routed to the loudspeaker. Held here, beside `muted`,
   * because the in-call bar and the full-screen call page are two views of the
   * same call and each can toggle it; it resets when the call ends so the next
   * call starts on the earpiece.
   */
  speakerOn: boolean;
  /** True when running on a native platform (Capacitor iOS/Android). Web is no-op. */
  supported: boolean;
  toggleMute: () => Promise<void>;
  /** Flips `speakerOn` optimistically and reverts it if the audio route fails. */
  toggleSpeaker: () => Promise<void>;
  /**
   * Blind-transfer the active call to another staff member (by membership
   * id) in the current property. The caller's leg is dropped server-side;
   * the resulting `callDisconnected` clears the local call state.
   */
  transferCall: (targetMemberId: string) => Promise<void>;
}

const VoiceContext = createContext<VoiceContextValue | null>(null);

export function VoiceProvider({ children }: { children: React.ReactNode }) {
  const supported = Capacitor.isNativePlatform();
  const history = useHistory();
  // The Twilio identity we mint server-side is `<memberId>__<propertyId>`,
  // so a property switch needs a fresh token + login. Subscribe to the
  // active property so the register effect re-runs on change.
  const { currentProperty } = useApp();
  const activePropertyId = currentProperty?.id ?? null;
  const [registered, setRegistered] = useState(false);
  const [activeCall, setActiveCall] = useState<ActiveCall | null>(null);
  const [pendingInvite, setPendingInvite] = useState<PendingInvite | null>(
    null
  );
  const activeCallRef = useRef<ActiveCall | null>(null);
  activeCallRef.current = activeCall;
  const historyRef = useRef(history);
  historyRef.current = history;

  useVoiceNativeListeners({
    activeCallRef,
    activePropertyId,
    historyRef,
    setActiveCall,
    setPendingInvite,
    setRegistered,
    supported,
    updateCall: setActiveCall,
  });

  const makeCall = useCallback<VoiceContextValue["makeCall"]>(
    async ({ to, displayName }) => {
      if (!supported) {
        toast.error(
          interfaceTranslator("communications.voiceContext")("errorUnsupported")
        );
        return;
      }
      if (!(await ensureMicGranted())) {
        return;
      }
      await CapacitorTwilioVoice.makeCall({ displayName, to });
    },
    [supported]
  );

  const acceptInvite = useCallback(async () => {
    if (!pendingInvite) {
      return;
    }
    if (isSimulatedCall(pendingInvite.callSid)) {
      setPendingInvite(null);
      return;
    }
    if (!(await ensureMicGranted())) {
      return;
    }
    await CapacitorTwilioVoice.acceptCall({ callSid: pendingInvite.callSid });
  }, [pendingInvite]);

  const rejectInvite = useCallback(async () => {
    if (!pendingInvite) {
      return;
    }
    if (isSimulatedCall(pendingInvite.callSid)) {
      setPendingInvite(null);
      return;
    }
    await CapacitorTwilioVoice.rejectCall({ callSid: pendingInvite.callSid });
    setPendingInvite(null);
  }, [pendingInvite]);

  const endCall = useCallback(async () => {
    const { current } = activeCallRef;
    if (current && isSimulatedCall(current.callSid)) {
      setActiveCall(null);
      return;
    }
    await (current
      ? CapacitorTwilioVoice.endCall({ callSid: current.callSid })
      : CapacitorTwilioVoice.endCall({}));
  }, []);

  const toggleMute = useCallback(async () => {
    const { current } = activeCallRef;
    if (!current) {
      return;
    }
    const nextMuted = !current.muted;
    if (!isSimulatedCall(current.callSid)) {
      await CapacitorTwilioVoice.muteCall({
        callSid: current.callSid,
        muted: nextMuted,
      });
    }
    setActiveCall((prev) => (prev ? { ...prev, muted: nextMuted } : prev));
  }, []);

  const [speakerOn, setSpeakerOn] = useState(false);
  useEffect(() => {
    if (!activeCall) {
      setSpeakerOn(false);
    }
  }, [activeCall]);

  const toggleSpeaker = useCallback(async () => {
    const { current } = activeCallRef;
    if (!current) {
      return;
    }
    const next = !speakerOn;
    setSpeakerOn(next);
    if (isSimulatedCall(current.callSid)) {
      return;
    }
    try {
      await CapacitorTwilioVoice.setSpeaker({ enabled: next });
    } catch (err) {
      console.error("[voice] speaker route failed", err);
      setSpeakerOn(!next);
    }
  }, [speakerOn]);

  const transferCall = useCallback<VoiceContextValue["transferCall"]>(
    async (targetMemberId) => {
      const t = interfaceTranslator("communications.voiceContext");
      const { current } = activeCallRef;
      if (!current) {
        toast.error(t("errorNoActiveCall"));
        return;
      }
      if (isSimulatedCall(current.callSid)) {
        toast.success("Call transferred (simulated)");
        setActiveCall(null);
        return;
      }
      try {
        await apiTransferCall({ callSid: current.callSid, targetMemberId });
        // The caller's leg is dropped server-side; the plugin's
        // callDisconnected event clears the UI. Nothing else to do here.
      } catch (err) {
        console.error("[voice] transfer failed", err);
        toast.error(t("errorNotTransferred"));
      }
    },
    []
  );

  const simulators = useCallSimulators({
    activePropertyId,
    historyRef,
    setActiveCall,
    setPendingInvite,
  });

  const value = useMemo<VoiceContextValue>(
    () => ({
      acceptInvite,
      activeCall,
      endCall,
      makeCall,
      pendingInvite,
      registered,
      rejectInvite,
      speakerOn,
      supported,
      toggleMute,
      toggleSpeaker,
      transferCall,
      ...simulators,
    }),
    [
      registered,
      supported,
      activeCall,
      pendingInvite,
      makeCall,
      acceptInvite,
      rejectInvite,
      endCall,
      toggleMute,
      speakerOn,
      toggleSpeaker,
      transferCall,
      simulators,
    ]
  );

  return (
    <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>
  );
}

export function useVoice(): VoiceContextValue {
  const ctx = useContext(VoiceContext);
  if (!ctx) {
    throw new Error("useVoice must be used within VoiceProvider");
  }
  return ctx;
}

/**
 * Build the `{ to, displayName }` payload that `voice.makeCall` expects
 * for a staff-to-staff call. The recipient's identity is scoped to the
 * **caller's** propertyId so the TwiML webhook rings the device session
 * registered under that property, and no other.
 *
 * Keeping this as a top-level helper (rather than a method on the voice
 * context) keeps `voice.makeCall` single-purpose: it doesn't need to
 * know about staff vs guest dispatch.
 */
export function buildStaffCallTarget(
  recipientMemberId: string,
  callerPropertyId: string,
  displayName?: string
): { to: string; displayName?: string } {
  return {
    displayName,
    to: `client:${encodeVoiceIdentity(recipientMemberId, callerPropertyId)}`,
  };
}
